<script lang="ts">
  import { throttle } from 'lodash-es'
  import { onMount } from 'svelte'
  import { on } from 'svelte/events'
  import { fade, scale } from 'svelte/transition'
  import type {
    HorizontalPosition,
    Position,
    Style,
    VerticalPosition,
  } from './types'

  interface Props {
    position: Position
    style: Style
    children?: import('svelte').Snippet
  }

  let { position: originalPosition, style, children }: Props = $props()

  // svelte-ignore state_referenced_locally
  let position = $state(originalPosition)

  let tooltipElement: HTMLDivElement | undefined = $state()

  const allHorizontalPositions: HorizontalPosition[] = [
    'center',
    'left',
    'right',
  ]
  const allVerticalPositions: VerticalPosition[] = ['bottom', 'top', 'middle']

  const allPositions = allHorizontalPositions
    .flatMap((h) => allVerticalPositions.map((v) => `${h}-${v}`))
    .filter((p) => p !== 'center-middle') as Position[]

  const getOverflows = () => {
    const rect = tooltipElement!.getBoundingClientRect()

    // Return an object for overflows in each direction
    return {
      top: Math.abs(Math.min(rect.top, 0)),
      bottom: Math.max(
        0,
        rect.bottom -
          (window.innerHeight || document.documentElement.clientHeight),
      ),
      left: Math.abs(Math.min(rect.left, 0)),
      right: Math.max(
        0,
        rect.right -
          (window.innerWidth || document.documentElement.clientWidth),
      ),
      get horizontal() {
        return Math.max(this.left, this.right)
      },
      get vertical() {
        return Math.max(this.top, this.bottom)
      },
      get all() {
        return this.horizontal + this.vertical
      },
      get any() {
        return this.top || this.bottom || this.left || this.right
      },
    }
  }

  const setPosition = (p: Position) => {
    position = p
    ;[...allHorizontalPositions, ...allVerticalPositions].forEach((pos) => {
      tooltipElement!.classList.remove(pos)
    })
    position.split('-').forEach((pos) => {
      tooltipElement!.classList.add(pos)
    })
  }

  const fixPositionIfNecessary = async () => {
    // Since we debounce this function, the tooltip might have been removed from the DOM by the time
    // it runs. In that case, we can just skip it.
    if (!tooltipElement) return

    let p = originalPosition

    setPosition(p)

    const overflows = getOverflows()

    if (!overflows.any) {
      // There is no overflow, so we're good.
      return
    }

    const positionsWithOverflows = allPositions.map((p) => {
      setPosition(p)
      return [p, getOverflows()] as [Position, ReturnType<typeof getOverflows>]
    })

    // Find the position with the least overflow
    const [bestPosition] = positionsWithOverflows.reduce((best, current) => {
      if (current[1].all < best[1].all) {
        return current
      }
      return best
    }, positionsWithOverflows[0]!)

    setPosition(bestPosition)
  }

  onMount(() => {
    fixPositionIfNecessary()

    const debouncedFix = throttle(fixPositionIfNecessary, 200)

    setTimeout(debouncedFix, 200)

    const unsubscribers = [
      on(document, 'scroll', debouncedFix),
      on(window, 'resize', debouncedFix),
    ]

    return () => {
      unsubscribers.forEach((unsub) => unsub())
    }
  })
</script>

<div
  bind:this={tooltipElement}
  class="tooltip {position.split('-').join(' ')} {style}"
  in:fade={{ duration: 50 }}
  out:scale={{ duration: 50, start: 0.8 }}
  onintrostart={fixPositionIfNecessary}
>
  {@render children?.()}

  <svg
    class="arrow"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 0H0L5.3 8.3C5.47937 8.58942 5.72965 8.82826 6.02715 8.99388C6.32465 9.1595 6.65951 9.24643 7 9.24643C7.34049 9.24643 7.67535 9.1595 7.97285 8.99388C8.27035 8.82826 8.52063 8.58942 8.7 8.3L14 0Z"
      fill="currentColor"
    />
  </svg>
</div>

<style lang="postcss">
  .tooltip {
    --_offset: 0.85rem;
    /* default: adapts to the theme */
    --_color-bg: var(--color-bg-surface);
    position: absolute;
    /* This is necessary for Safari so the box shadow doesn't stay visible. */
    transform: translateZ(0);
    z-index: 10;
    filter: drop-shadow(0 0.125rem 0.25rem #0001)
      drop-shadow(0.25rem 0.25rem 0.5rem #0001) drop-shadow(0 0 0.0625rem #0003);
    border: 1px solid var(--color-border);
    border-radius: 0.25rem;
    background-color: var(--_color-bg);
    padding: 0.5rem 1rem;
    width: max-content;
    max-width: min(95vw, 18rem);
    color: var(--color-text);
    font-size: var(--scale-00);
    line-height: 150%;
    white-space: normal;

    /* status: always dark, in both themes */
    &.status {
      --_color-bg: var(--color-bg-dark);
      filter: none;
      border: none;
      color: var(--color-text-ondark);
    }

    &.bottom {
      top: calc(100% + var(--_offset));
    }
    &.top {
      bottom: calc(100% + var(--_offset));
    }
    &.center {
      left: 50%;
      translate: -50% 0;
    }
    &.middle {
      top: 50%;
      translate: 0 -50%;
    }

    &.right:not(.middle) {
      left: 0;
    }
    &.right.middle {
      left: calc(100% + var(--_offset));
    }
    &.left:not(.middle) {
      right: 0;
    }
    &.left.middle {
      right: calc(100% + var(--_offset));
    }
  }
  .arrow {
    --_size: 0.875rem;
    --_offset: -0.85rem;
    display: block;
    position: absolute;
    margin: 0;
    inset: auto;
    padding: 0;
    width: var(--_size);
    height: var(--_size);
    color: var(--_color-bg);

    .middle & {
      top: 50%;
      translate: 0 -50%;
    }
    .center & {
      left: 50%;
      translate: -50% 0;
    }

    /* In only two positions, the arrow points to the side. */
    .left.middle & {
      right: var(--_offset);
      rotate: -0.25turn;
    }
    .right.middle & {
      left: var(--_offset);
      rotate: 0.25turn;
    }

    .bottom & {
      top: var(--_offset);
      rotate: 0.5turn;
    }
    .top & {
      bottom: var(--_offset);
    }
    .left:not(.middle) & {
      right: 0.5rem;
    }
    .right:not(.middle) & {
      left: 0.5rem;
    }
  }
</style>
